import { IconV1 } from 'common/icon/iconV1.js';
import * as React from 'react';
import { useAppDispatch, useAppSelector } from 'redux-state/configure-store.js';
import { REDUX_FAVOURITES_ADD_ASYNC, REDUX_FAVOURITES_REMOVE } from 'redux-state/favourites/reducer.js';
import { createFavouriteApi } from 'redux-state/favourites/types.js';
import { PW_FAVOURITE_ADD_REMOVE_BUTTON } from 'utilities/consts/variable-types.js';
import { FavouriteCurrentLocationHook } from '../hooks/location-favourite-properties.js';
import { UsePageFavouriteProps } from '../hooks/page-favourite-properties.js';

const FAVOURITE_LOCATION = 'Click to favourite this location';
const UNFAVOURITE_LOCATION = 'Click to remove from favourites';

/** Displays favourite icons (favourited locations, star and add) on search bar and associated popups to manage favourites.
 *  tiny popup children MUST support forwardRef
 */
export const FavPopupChildFavAdded = React.forwardRef<
  HTMLButtonElement,
  {
    setPreviewLocationFavourite: (b: boolean) => void;
  }
>((props, ref) => {
  const { setPreviewLocationFavourite } = props;
  const appDispatch = useAppDispatch();
  const favTop = useAppSelector((s) => s.FAVOURITES.topItems);
  const location = useAppSelector((state) => state.LAYOUT.screen?.location);
  const { isPageFavourited } = UsePageFavouriteProps();
  const { type, key, favouriteKey, favouriteType } = FavouriteCurrentLocationHook({ location });

  const onFavouriteClick = React.useCallback(
    (e: React.SyntheticEvent, addFav: boolean): void => {
      e.stopPropagation();
      e.preventDefault();
      if (type && key) {
        const favouriteApi = createFavouriteApi({ type, key, favouriteKey, favouriteType });
        if (favouriteApi) {
          addFav
            ? appDispatch(REDUX_FAVOURITES_ADD_ASYNC({ key: favouriteApi }))
            : appDispatch(REDUX_FAVOURITES_REMOVE({ key: favouriteApi }));
        }
      }
    },
    [appDispatch, favouriteKey, favouriteType, key, type],
  );

  const onFavouriteMouseOverHandler = React.useCallback(() => {
    if (!favTop.find((e) => e.key === location?.key)) {
      /** location is not if top favourites so preview it */
      setPreviewLocationFavourite(true);
    }
  }, [favTop, location, setPreviewLocationFavourite]);

  const onFavouriteMouseOutHandler = React.useCallback(() => {
    setPreviewLocationFavourite(false);
  }, [setPreviewLocationFavourite]);

  if (isPageFavourited) {
    return (
      <button
        ref={ref}
        className="SearchBar-actions-star is-favourite"
        onClick={(e) => onFavouriteClick(e, false)}
        title={UNFAVOURITE_LOCATION}
        data-testid={PW_FAVOURITE_ADD_REMOVE_BUTTON}
      >
        <IconV1 icon="favourite-solid" iconClass="svg--fill-inherit" />
      </button>
    );
  }

  return (
    <button
      ref={ref}
      className="SearchBar-actions-star"
      onClick={(e) => onFavouriteClick(e, true)}
      onMouseOver={onFavouriteMouseOverHandler}
      onMouseOut={onFavouriteMouseOutHandler}
      title={FAVOURITE_LOCATION}
      data-testid={PW_FAVOURITE_ADD_REMOVE_BUTTON}
    >
      <IconV1 icon="favourite-outline" iconClass="svg--fill-inherit" />
      <span className="u-hiddenVisually">{FAVOURITE_LOCATION}</span>
    </button>
  );
});
